import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { celebrate } from '../lib/celebrate';
import { X, Mail, Lock, User, Loader2, GraduationCap, UserCheck, ArrowRight, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';

const ROLES = [
  { v: 'eleve', label: 'Élève', icon: GraduationCap, desc: 'Je prépare mon permis B' },
  { v: 'enseignant', label: 'Enseignant ECSR', icon: UserCheck, desc: "J'enseigne la conduite" },
];

export default function AuthModal() {
  const { modalOpen, modalMode, closeModal, setModalMode, login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'eleve' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [view, setView] = useState('auth'); // 'auth' | 'forgot' | 'forgotSent'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotErr, setForgotErr] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  if (!modalOpen) return null;
  const isLogin = modalMode === 'login';

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        const u = await login({ email: form.email, password: form.password });
        handleClose();
        if (u?.is_admin) navigate('/admin');
        else if (u?.role === 'secretaire') navigate('/secretariat');
        else if (u?.role === 'enseignant') navigate('/enseignant/planning');
      } else {
        const res = await register(form);
        // Compte enseignant en attente de validation admin → affichage message dédié
        if (res && res.pending) {
          setView('pending');
          return;
        }
        // 🎉 Fêter la création du compte
        setTimeout(() => celebrate(), 60);
        handleClose();
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotErr(''); setForgotLoading(true);
    try {
      await api.requestPasswordReset(forgotEmail.trim());
      setView('forgotSent');
    } catch (err) {
      setForgotErr(err?.response?.data?.detail || 'Erreur. Réessayez.');
    } finally { setForgotLoading(false); }
  };

  const handleClose = () => {
    setForm({ email: '', password: '', name: '', role: 'eleve' });
    setError(''); setShowPwd(false); setView('auth');
    setForgotEmail(''); setForgotErr('');
    closeModal();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-[#0d0d0e] border border-white/10 rounded-2xl shadow-2xl shadow-red-900/20 overflow-hidden">
        <button data-testid="auth-close" onClick={handleClose} aria-label="Fermer" className="absolute top-4 right-4 text-white/50 hover:text-white z-10"><X size={18} /></button>

        <div className="p-7 lg:p-9">
          {view === 'auth' && (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">{isLogin ? 'Connexion' : 'Création de compte'}</div>
              <h2 id="auth-modal-title" className="font-serif-editorial text-white text-[28px] leading-tight">{isLogin ? 'Re-bienvenue !' : 'Crée ton compte gratuit'}</h2>
              <p className="text-white/55 text-[13.5px] mt-2">{isLogin ? 'Reprends ton parcours là où tu l\u2019avais laissé.' : '100 % gratuit, sans pub, sans tracking.'}</p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                {!isLogin && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Prénom</label>
                    <div className="mt-1 relative">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input data-testid="auth-name" required value={form.name} onChange={(e) => update('name', e.target.value)} type="text" placeholder="Bruno" className="w-full h-11 pl-9 pr-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Email</label>
                  <div className="mt-1 relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input data-testid="auth-email" required value={form.email} onChange={(e) => update('email', e.target.value)} type="email" autoComplete="email" placeholder="ton@email.fr" className="w-full h-11 pl-9 pr-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Mot de passe</label>
                    {isLogin && (
                      <button type="button" data-testid="auth-forgot-link" onClick={() => { setView('forgot'); setForgotEmail(form.email); }} className="text-[11px] text-red-400 hover:text-red-300 font-semibold">
                        Mot de passe oublié ?
                      </button>
                    )}
                  </div>
                  <div className="mt-1 relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="auth-password"
                      required minLength={isLogin ? 1 : 8}
                      value={form.password}
                      onChange={(e) => update('password', e.target.value)}
                      type={showPwd ? 'text' : 'password'}
                      autoComplete={isLogin ? 'current-password' : 'new-password'}
                      placeholder={isLogin ? 'Ton mot de passe' : 'Min. 8 caractères'}
                      className="w-full h-11 pl-9 pr-11 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition"
                    />
                    <button
                      type="button"
                      data-testid="auth-toggle-pwd"
                      onClick={() => setShowPwd((v) => !v)}
                      tabIndex={-1}
                      aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.05]"
                    >
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Je suis</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {ROLES.map((r) => {
                        const Icon = r.icon;
                        const selected = form.role === r.v;
                        return (
                          <button type="button" key={r.v} data-testid={`auth-role-${r.v}`} onClick={() => update('role', r.v)} className={`p-3 rounded-lg border text-left transition ${selected ? 'border-red-500 bg-red-600/15' : 'border-white/10 bg-white/5 hover:border-white/30'}`}>
                            <Icon size={16} className={selected ? 'text-red-400' : 'text-white/70'} />
                            <div className="text-[12.5px] text-white font-semibold mt-2">{r.label}</div>
                            <div className="text-[10.5px] text-white/50 mt-0.5 leading-tight">{r.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {error && <div data-testid="auth-error" className="text-red-400 text-[12.5px] bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">{error}</div>}

                <button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-red-900/40 disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <>{isLogin ? 'Se connecter' : 'Créer mon compte'} <ArrowRight size={15} /></>}
                </button>
              </form>

              <div className="mt-5 text-center text-[13px] text-white/60">
                {isLogin ? (
                  <>Pas encore de compte ? <button data-testid="auth-switch-register" onClick={() => setModalMode('register')} className="text-red-400 hover:text-red-300 font-semibold">Créer un compte</button></>
                ) : (
                  <>Déjà inscrit ? <button data-testid="auth-switch-login" onClick={() => setModalMode('login')} className="text-red-400 hover:text-red-300 font-semibold">Se connecter</button></>
                )}
              </div>
            </>
          )}

          {view === 'forgot' && (
            <>
              <button onClick={() => setView('auth')} className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-[12.5px] mb-4">
                <ArrowLeft size={13} /> Retour à la connexion
              </button>
              <div className="text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Mot de passe oublié</div>
              <h2 className="font-serif-editorial text-white text-[26px] leading-tight">Pas de panique.</h2>
              <p className="text-white/60 text-[13.5px] mt-2">Indique ton email : on t&apos;envoie un lien pour choisir un nouveau mot de passe (valable 1h).</p>
              <form onSubmit={submitForgot} className="mt-6 space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Email</label>
                  <div className="mt-1 relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="forgot-email-input"
                      required type="email" autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="ton@email.fr"
                      className="w-full h-11 pl-9 pr-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition"
                    />
                  </div>
                </div>
                {forgotErr && <div className="text-red-400 text-[12.5px] bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">{forgotErr}</div>}
                <button data-testid="forgot-submit" type="submit" disabled={forgotLoading || !forgotEmail.trim()} className="w-full h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-red-900/40 disabled:opacity-50">
                  {forgotLoading ? <Loader2 size={16} className="animate-spin" /> : <>Envoyer le lien <ArrowRight size={15} /></>}
                </button>
              </form>
            </>
          )}

          {view === 'pending' && (
            <div data-testid="auth-pending">
              <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300 mb-4">
                <CheckCircle2 size={22} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400 font-bold mb-2">Compte en attente</div>
              <h2 className="font-serif-editorial text-white text-[26px] leading-tight">Inscription enregistrée !</h2>
              <p className="text-white/70 text-[14px] mt-3 leading-relaxed">
                Merci <strong className="text-white">{form.name}</strong>. Ton compte enseignant est bien créé mais doit être <strong className="text-amber-300">validé par l&apos;administrateur principal</strong> avant de pouvoir accéder à la plateforme.
              </p>
              <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3.5 text-[12.5px] text-white/70 leading-relaxed">
                <p>👉 Tu recevras un email dès que ton compte sera activé (généralement sous 24 h ouvrées).</p>
                <p className="mt-2">Pour accélérer la validation, contacte directement <a href="mailto:contact@codevoiture.fr" className="text-red-400 hover:text-red-300">contact@codevoiture.fr</a> avec ton n° d&apos;autorisation d&apos;enseigner.</p>
              </div>
              <button onClick={handleClose} className="mt-6 w-full h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13.5px] font-semibold">Fermer</button>
            </div>
          )}

          {view === 'forgotSent' && (
            <div data-testid="forgot-sent">
              <button onClick={() => { setView('auth'); }} className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-[12.5px] mb-4">
                <ArrowLeft size={13} /> Retour à la connexion
              </button>
              <div className="w-14 h-14 rounded-full bg-green-600/15 border border-green-700/30 flex items-center justify-center text-green-400 mb-4">
                <CheckCircle2 size={22} />
              </div>
              <h2 className="font-serif-editorial text-white text-[26px] leading-tight">Email envoyé !</h2>
              <p className="text-white/65 text-[14px] mt-3 leading-relaxed">
                Si <strong className="text-white">{forgotEmail}</strong> est inscrit chez nous, un lien de réinitialisation vient d&apos;être envoyé. Vérifie ta boîte de réception (et tes spams !) — lien valable 1 heure.
              </p>
              <p className="text-white/45 text-[12px] mt-5">
                Tu n&apos;as rien reçu après 5 minutes ? Vérifie l&apos;orthographe de l&apos;email ou contacte <a href="mailto:contact@codevoiture.fr" className="text-red-400 hover:text-red-300">contact@codevoiture.fr</a>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
